async function wait (ms: number): Promise<void> {
  return await new Promise((resolve) => setTimeout(resolve, ms))
}

async function waitRandom (min: number, max: number): Promise<void> {
  return await wait(min + Math.round(Math.random() * Math.max(0, max - min)))
}

/**
 * This error is thrown if the function is cancelled before completing
 */
export class CancelledError extends Error {
  public isCancelledError: boolean

  constructor () {
    super('Cancelled')
    this.isCancelledError = true

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, CancelledError)
    }
  }
}

/**
 * Throw this error if the function should retry
 */
export class RetryableError extends Error {
  constructor (message: string) {
    super(message)

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, RetryableError)
    }
  }
}

/**
 * Retries the function that returns the promise until the promise successfully resolves up to n retries
 * @param fn function to retry
 * @param n how many times to retry
 * @param minWait min wait between retries in ms
 * @param maxWait max wait between retries in ms
 */
export function retry<T> (
  fn: () => Promise<T>,
  { n, minWait, maxWait }: { n: number, minWait: number, maxWait: number }
): { promise: Promise<T>, cancel: () => void } {
  let completed = false
  let rejectCancelled: (error: Error) => void

  const promise = new Promise<T>((resolve, reject) => {
    rejectCancelled = reject

    void (async () => {
      let remainingAttempts = n

      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (completed) break

        try {
          const result = await fn()
          if (!completed) {
            resolve(result)
            completed = true
          }
          break
        } catch (error) {
          if (completed) {
            break
          }

          // If we've run out of attempts or it's not a retryable error, reject
          if (remainingAttempts <= 0 || !(error instanceof RetryableError)) {
            reject(error)
            completed = true
            break
          }

          remainingAttempts--
          console.debug(`Retrying, ${remainingAttempts} attempts remaining`)
        }

        if (!completed) {
          await waitRandom(minWait, maxWait)
        }
      }
    })()
  })

  return {
    promise,
    cancel: () => {
      if (completed) return
      completed = true
      const cancelError = new CancelledError()
      rejectCancelled(cancelError)
    }
  }
}
